import { useState, useRef, useCallback, useEffect, useId } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUp, Check, X, Stop, Microphone, At } from "@phosphor-icons/react";
import { LeverMark } from "../../lib/brand";
import { useInlineAssistantStore } from "../../stores/inlineAssistant";
import { useDocumentStore } from "../../stores/document";
import { useEditorStore } from "../../stores/editor";
import { cn } from "../../lib/cn";
import type { AgentStatus, EditEvent } from "../../types/ai";
import { springSnappy } from "../../lib/motion-config";
import { useMentions, type MentionableFile } from "../../hooks/useMentions";
import { MentionDropdown } from "./MentionUI";
import { RichChatInput, type RichChatInputHandle } from "../../components/ai/RichChatInput";

const spring = springSnappy;

const STATUS_LABELS: Partial<Record<AgentStatus, string>> = {
  thinking: "Thinking",
  reading: "Reading document",
  compiling: "Compiling",
  editing: "Editing",
};

const MAX_RESPONSE_CHARS = 180;

function truncate(text: string): string {
  if (text.length <= MAX_RESPONSE_CHARS) return text;
  return text.slice(0, MAX_RESPONSE_CHARS).trimEnd() + "\u2026";
}

interface ClassContext {
  courseName: string;
  dueDate?: string;
  assignmentType?: string;
}

const ESSAY_QUICK = [
  { label: "Outline", message: "Create an outline for this essay based on the assignment requirements" },
  { label: "Revise", message: "Revise my current draft to improve clarity and style" },
  { label: "Strengthen thesis", message: "Strengthen the thesis statement in my essay" },
];

const PSET_QUICK = [
  { label: "Hint", message: "Give me a hint for the problem I'm working on" },
  { label: "Check work", message: "Check my work on the current problem" },
  { label: "Explain concept", message: "Explain the key concept behind this problem" },
];

const GENERAL_QUICK = [
  { label: "Get started", message: "Help me get started based on the assignment requirements" },
  { label: "Improve", message: "Improve the writing in my document" },
  { label: "Check requirements", message: "Check if my document meets the assignment requirements" },
];

function getClassQuickActions(assignmentType?: string) {
  if (assignmentType === "essay") return ESSAY_QUICK;
  if (assignmentType === "pset") return PSET_QUICK;
  return GENERAL_QUICK;
}

interface InlineAssistantProps {
  projectId?: string;
  className?: string;
  classContext?: ClassContext;
  files?: MentionableFile[];
  activeFileId?: string;
}

export function InlineAssistant({ projectId, className, classContext, files, activeFileId }: InlineAssistantProps) {
  const isOpen = useInlineAssistantStore((s) => s.isOpen);
  const agentStatus = useInlineAssistantStore((s) => s.agentStatus);
  const streamingContent = useInlineAssistantStore((s) => s.streamingContent);
  const lastResponse = useInlineAssistantStore((s) => s.lastResponse);
  const pendingProposal = useInlineAssistantStore((s) => s.pendingProposal);
  const sendMessage = useInlineAssistantStore((s) => s.sendMessage);
  const cancelStream = useInlineAssistantStore((s) => s.cancelStream);
  const close = useInlineAssistantStore((s) => s.close);
  const dismissProposal = useInlineAssistantStore((s) => s.dismissProposal);
  const clearResponse = useInlineAssistantStore((s) => s.clearResponse);

  const currentDocument = useDocumentStore((s) => s.currentDocument);
  const proposePendingEdit = useDocumentStore((s) => s.proposePendingEdit);
  const acceptPendingEdit = useDocumentStore((s) => s.acceptPendingEdit);
  const rejectPendingEdit = useDocumentStore((s) => s.rejectPendingEdit);
  const docPendingEdit = useDocumentStore((s) => s.pendingEdit);
  const cursorLine = useEditorStore((s) => s.cursorLine);
  const selection = useEditorStore((s) => s.selection);

  const [hasText, setHasText] = useState(false);
  const inputRef = useRef<RichChatInputHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uid = useId();
  const btnGradId = `ia-btn-${uid}`;

  const mentions = useMentions({ files, activeFileId });

  const isProcessing = agentStatus !== "idle" && agentStatus !== "done";
  const displayResponse = lastResponse || streamingContent;
  const hasResponse = displayResponse.length > 0;
  const showResponse = hasResponse && !isProcessing;
  const showAcceptReject = showResponse && pendingProposal && docPendingEdit;
  const showInputRow = !isProcessing && !showResponse;

  useEffect(() => {
    if (isOpen && !isProcessing && !showResponse) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen, isProcessing, showResponse]);

  // ─── Select file from dropdown → insert inline chip ───

  const handleSelectFile = useCallback(
    (file: MentionableFile) => {
      inputRef.current?.insertChip(file);
      mentions.addMention(file);
    },
    [mentions],
  );

  // ─── Send ───

  const handleSend = useCallback(() => {
    const content = inputRef.current?.getContent();
    if (!content?.text || isProcessing) return;

    sendMessage({
      message: content.text,
      documentId: currentDocument?.id,
      projectId,
      referencedFileIds: content.fileIds ?? mentions.getMentionedFileIds(),
      cursorLine,
      selectedText: selection?.text,
      selectionStart: selection?.from,
      selectionEnd: selection?.to,
      onEdit: (edit: EditEvent) => {
        const proposed = edit.full_new_content ?? edit.newContent ?? edit.content;
        if (proposed && currentDocument) {
          proposePendingEdit(proposed, content.text.slice(0, 60));
        }
      },
    });

    inputRef.current?.clear();
    mentions.resetMentions();
  }, [isProcessing, sendMessage, currentDocument, projectId, cursorLine, selection, proposePendingEdit, mentions]);

  // ─── Keyboard routing ───

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Mention dropdown navigation
      if (mentions.showMentionMenu && mentions.filteredFiles.length > 0) {
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          const file = mentions.getHighlightedFile();
          if (file) handleSelectFile(file);
          return;
        }
        if (mentions.handleMentionKeyDown(e)) return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (isProcessing) {
          cancelStream();
        } else if (showResponse) {
          clearResponse();
        } else {
          close();
        }
      }
    },
    [handleSend, isProcessing, cancelStream, close, showResponse, clearResponse, mentions, handleSelectFile],
  );

  // ─── Mention trigger from RichChatInput ───

  const handleMentionTrigger = useCallback(
    (filter: string | null) => {
      mentions.setMentionTrigger(filter);
    },
    [mentions],
  );

  // ─── Chip lifecycle sync ───

  const handleChipRemoved = useCallback(
    (fileId: string) => {
      mentions.removeMention(fileId);
    },
    [mentions],
  );

  const handleAccept = useCallback(() => {
    acceptPendingEdit();
    dismissProposal();
  }, [acceptPendingEdit, dismissProposal]);

  const handleReject = useCallback(() => {
    rejectPendingEdit();
    dismissProposal();
  }, [rejectPendingEdit, dismissProposal]);

  const handlePlusClick = useCallback(() => {
    if (!isProcessing) {
      fileInputRef.current?.click();
    }
  }, [isProcessing]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // TODO: handle file attachment properly
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [],
  );

  const handleResponseClick = useCallback(() => {
    clearResponse();
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [clearResponse]);

  const preventEditorFocus = useCallback(
    (e: React.MouseEvent | React.PointerEvent) => {
      e.stopPropagation();
    },
    [],
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.92 }}
          transition={spring}
          onMouseDown={preventEditorFocus}
          onPointerDown={preventEditorFocus}
          className={cn(
            "flex flex-col w-full max-w-[560px] mx-auto",
            "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-md)]",
            "border border-[var(--border-subtle)]",
            "rounded-2xl",
            isProcessing
              ? "shadow-[var(--glow-ai)]"
              : "shadow-[var(--shadow-lg)]",
            className,
          )}
        >
          {/* Class context (above input row) */}
          {classContext && (
            <div className="px-3 pt-1.5 space-y-1">
              <div className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-lever-orange)]/8 px-2 py-0.5 text-[var(--color-lever-orange)] font-medium">
                  {classContext.courseName}
                </span>
                {classContext.dueDate && (
                  <span className="opacity-60">Due {classContext.dueDate}</span>
                )}
              </div>
              {!hasText && !isProcessing && !hasResponse && (
                <div className="flex gap-1 flex-wrap">
                  {getClassQuickActions(classContext.assignmentType).map((action) => (
                    <button
                      key={action.label}
                      onClick={() => {
                        // Insert text into the rich input
                        inputRef.current?.clear();
                        inputRef.current?.focus();
                        // Use execCommand to insert text into contentEditable
                        document.execCommand("insertText", false, action.message);
                      }}
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[9px] font-medium",
                        "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
                        "hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]",
                        "border border-[var(--border-hairline)]",
                        "transition-colors duration-100",
                      )}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Single input row */}
          <div className="relative flex items-center gap-1.5 px-2 py-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept=".pdf,.tex,.md,.txt,.bib,.png,.jpg,.jpeg"
            />

            {/* Left button: Plus (idle) / Logo+ring (processing) */}
            <motion.button
              onClick={handlePlusClick}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.88 }}
              transition={spring}
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                "transition-colors duration-150",
                isProcessing
                  ? "text-[var(--color-lever-orange)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              )}
              title="Attach context file"
            >
              <AnimatePresence mode="wait" initial={false}>
                {isProcessing ? (
                  <motion.div
                    key="logo"
                    initial={{ scale: 0.4, opacity: 0, rotate: -180 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    exit={{ scale: 0.4, opacity: 0, rotate: 180 }}
                    transition={{ type: "spring", stiffness: 400, damping: 22 }}
                    className="relative flex items-center justify-center w-6 h-6"
                  >
                    <svg
                      className="thinking-ring absolute inset-0"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="11"
                        stroke={`url(#${btnGradId})`}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeDasharray="16 50"
                      />
                      <defs>
                        <linearGradient
                          id={btnGradId}
                          x1="0"
                          y1="0"
                          x2="24"
                          y2="24"
                        >
                          <stop stopColor="var(--color-lever-orange)" />
                          <stop
                            offset="1"
                            stopColor="var(--color-lever-orange)"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                    <LeverMark size={10} className="thinking-logo" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="plus"
                    initial={{ scale: 0.4, opacity: 0, rotate: 90 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    exit={{ scale: 0.4, opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Plus size={14} weight="bold" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Center area: rich input (idle) / thinking shimmer (processing) / response text (done) */}
            <div className="flex-1 min-w-0 min-h-8 flex items-center relative">
              <AnimatePresence mode="wait" initial={false}>
                {isProcessing && !hasResponse ? (
                  <motion.div
                    key="thinking"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="flex items-center gap-2 px-1 w-full"
                  >
                    <span className="text-[12px] font-medium thinking-shimmer">
                      {STATUS_LABELS[agentStatus] || "Thinking"}
                      <span className="thinking-dots" />
                    </span>
                  </motion.div>
                ) : isProcessing && hasResponse ? (
                  <motion.div
                    key="streaming"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="flex items-center px-1 w-full"
                  >
                    <span className="text-[12px] leading-relaxed text-[var(--text-secondary)] line-clamp-1">
                      {truncate(displayResponse)}
                      <motion.span
                        className="inline-block w-[2px] h-[0.9em] bg-[var(--color-lever-orange)] ml-0.5 align-text-bottom rounded-full"
                        animate={{ opacity: [1, 0] }}
                        transition={{
                          duration: 0.6,
                          repeat: Infinity,
                          repeatType: "reverse",
                        }}
                      />
                    </span>
                  </motion.div>
                ) : showResponse ? (
                  <motion.div
                    key="response"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    onClick={!showAcceptReject ? handleResponseClick : undefined}
                    className={cn(
                      "flex items-center px-1 w-full",
                      !showAcceptReject && "cursor-pointer hover:opacity-80",
                    )}
                    title={showAcceptReject ? undefined : "Click to dismiss and type again"}
                  >
                    <span className="text-[12px] leading-relaxed text-[var(--text-secondary)] line-clamp-2">
                      {truncate(displayResponse)}
                    </span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="input"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="flex items-center w-full"
                  >
                    <RichChatInput
                      ref={inputRef}
                      onKeyDown={handleKeyDown}
                      onMentionTrigger={handleMentionTrigger}
                      onChipRemoved={handleChipRemoved}
                      onContentChange={setHasText}
                      placeholder="Edit with AI…"
                      disabled={isProcessing}
                      className={cn(
                        "w-full bg-transparent px-1 py-1.5",
                        "text-[12px] text-[var(--text-primary)]",
                      )}
                      style={{
                        fontFamily: "var(--font-sans)",
                        lineHeight: "1.5",
                      }}
                      maxHeight={120}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Mention dropdown (floats above the inline assistant) */}
              <MentionDropdown
                menuRef={mentions.menuRef}
                show={mentions.showMentionMenu}
                filteredFiles={mentions.filteredFiles}
                mentionFilter={mentions.mentionFilter}
                highlightIndex={mentions.mentionHighlight}
                onSelect={handleSelectFile}
                onHighlight={mentions.setMentionHighlight}
                position="above"
                compact
              />
            </div>

            {/* @ button (shown when idle and files available) */}
            {mentions.hasFiles && showInputRow && (
              <motion.button
                onClick={() => {
                  mentions.toggleMentionMenu();
                  inputRef.current?.focus();
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.88 }}
                transition={spring}
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                  "transition-colors duration-150",
                  mentions.showMentionMenu
                    ? "bg-[var(--color-lever-orange)]/10 text-[var(--color-lever-orange)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                )}
                title="Reference a file (@)"
              >
                <At size={12} weight="bold" />
              </motion.button>
            )}

            {/* Right button(s) */}
            {showAcceptReject ? (
              <div className="flex items-center gap-1 shrink-0">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.88 }}
                  transition={spring}
                  onClick={handleAccept}
                  className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-full",
                    "bg-[var(--color-signal-green)]/15 text-[var(--color-signal-green)]",
                    "hover:bg-[var(--color-signal-green)]/25",
                    "transition-colors duration-100",
                  )}
                  title="Accept changes"
                >
                  <Check size={13} weight="bold" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.88 }}
                  transition={spring}
                  onClick={handleReject}
                  className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-full",
                    "text-[var(--text-muted)]",
                    "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-secondary)]",
                    "transition-colors duration-100",
                  )}
                  title="Reject changes"
                >
                  <X size={13} weight="bold" />
                </motion.button>
              </div>
            ) : (
              <motion.button
                onClick={
                  isProcessing ? cancelStream : hasText ? handleSend : undefined
                }
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.88 }}
                transition={spring}
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  "transition-colors duration-150",
                  isProcessing
                    ? "bg-[var(--color-soft-red)]/15 text-[var(--color-soft-red)]"
                    : hasText
                      ? "bg-[var(--text-primary)] text-[var(--text-inverse)]"
                      : "bg-[var(--bg-surface-hover)] text-[var(--text-muted)] border border-[var(--border-hairline)]",
                )}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isProcessing ? (
                    <motion.div
                      key="stop"
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.4, opacity: 0 }}
                      transition={{ duration: 0.12 }}
                    >
                      <Stop size={12} weight="fill" />
                    </motion.div>
                  ) : hasText ? (
                    <motion.div
                      key="arrow"
                      initial={{ scale: 0.5, opacity: 0, rotate: -90 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0 }}
                      exit={{ scale: 0.5, opacity: 0, rotate: 90 }}
                      transition={{ duration: 0.12 }}
                    >
                      <ArrowUp size={14} weight="bold" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="mic"
                      initial={{ scale: 0.5, opacity: 0, rotate: 90 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0 }}
                      exit={{ scale: 0.5, opacity: 0, rotate: -90 }}
                      transition={{ duration: 0.12 }}
                    >
                      <Microphone size={14} weight="bold" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
